const DANGEROUS_EVENT_HANDLERS = [
  'click',
  'load',
  'error',
  'submit',
  'focus',
  'blur',
  'change',
  'mouseover',
  'mouseout',
  'mouseenter',
  'mouseleave',
  'keydown',
  'keyup',
  'keypress',
  'dblclick',
  'contextmenu',
  'abort',
  'resize',
  'scroll',
];
const ON_EVENT_PATTERN = new RegExp(`on(${DANGEROUS_EVENT_HANDLERS.join('|')})\\s*=\\s*["']?[^"']*["']?`, 'gi');

function sanitizeInputCore(input: string, trim: boolean): string {
  const out = input
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<(iframe|object|embed|form|link|meta|base)\b[^>]*>/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(ON_EVENT_PATTERN, '')
    .replace(/data\s*:\s*text\/html/gi, '');
  return trim ? out.trim() : out;
}

export function sanitizeInput(input: string): string {
  return sanitizeInputCore(input, true);
}

export function sanitizeInputMultiline(input: string): string {
  return sanitizeInputCore(input, false);
}

export function sanitizeForHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

export function sanitizePlaceName(input: string): string {
  return sanitizeInput(input);
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

export function validatePasswordStrength(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (!/[^A-Za-z0-9]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function sanitizeErrorMessage(error: unknown): string {
  const errorObj = error as { message?: string };

  if (process.env.NODE_ENV === 'production') {
    if (errorObj?.message?.includes('network')) {
      return 'Network error occurred. Please check your connection and try again.';
    }
    if (errorObj?.message?.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    if (errorObj?.message?.includes('auth')) {
      return 'Authentication error. Please try logging in again.';
    }
    return 'An unexpected error occurred. Please try again.';
  }

  if (process.env.NODE_ENV === 'staging') {
    return errorObj?.message || 'Unknown error occurred';
  }

  return errorObj?.message || 'Unknown error occurred';
}

export function isValidStripeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ['checkout.stripe.com', 'billing.stripe.com'].includes(parsed.hostname);
  } catch {
    return false;
  }
}

export function isValidInternalUrl(url: string): boolean {
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    const urlObj = new URL(url);
    return urlObj.origin === window.location.origin;
  } catch {
    return false;
  }
}

export class RateLimiter {
  private attempts: Map<string, { count: number; resetTime: number }> = new Map();

  constructor(
    private maxAttempts: number = 5,
    private windowMs: number = 15 * 60 * 1000
  ) {}

  canAttempt(key: string): boolean {
    const now = Date.now();
    const attempt = this.attempts.get(key);

    if (!attempt || now > attempt.resetTime) {
      this.attempts.set(key, { count: 1, resetTime: now + this.windowMs });
      return true;
    }

    if (attempt.count >= this.maxAttempts) {
      return false;
    }

    attempt.count++;
    return true;
  }

  reset(key: string): void {
    this.attempts.delete(key);
  }
}

export class AdminRateLimiter extends RateLimiter {
  constructor(maxAttempts = 5, windowMs: number = 15 * 60 * 1000) {
    super(maxAttempts, windowMs);
  }

  getRateLimitKey(identifier: string): string {
    return `admin_rate_limit_${identifier}`;
  }
}

export function generateRateLimitIdentifier(email?: string): string {
  if (email) {
    return `email_${email.toLowerCase().trim()}`;
  }
  return `ip_unknown`;
}

export class CSRFProtection {
  private static generateToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  static getToken(): string {
    let token = sessionStorage.getItem('csrf_token');
    if (!token) {
      token = this.generateToken();
      sessionStorage.setItem('csrf_token', token);
    }
    return token;
  }

  static validateToken(token: string): boolean {
    const storedToken = sessionStorage.getItem('csrf_token');
    return token === storedToken;
  }

  static clearToken(): void {
    sessionStorage.removeItem('csrf_token');
  }
}

const ON_EVENT_MALICIOUS_PATTERN = new RegExp(`on(${DANGEROUS_EVENT_HANDLERS.join('|')})\\s*=`, 'gi');

export function containsMaliciousPatterns(input: string): boolean {
  ON_EVENT_MALICIOUS_PATTERN.lastIndex = 0;
  const maliciousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    ON_EVENT_MALICIOUS_PATTERN,
    /data:text\/html/gi,
    /vbscript:/gi,
    /<iframe/gi,
    /<object/gi,
    /<embed/gi,
    /<form/gi,
    /<input/gi,
    /<textarea/gi,
    /<select/gi,
    /<button/gi,
    /<link/gi,
    /<meta/gi,
    /<base/gi,
  ];

  return maliciousPatterns.some((pattern) => pattern.test(input));
}

export function validateSessionIntegrity(): boolean {
  try {
    const sessionStart = localStorage.getItem('session_start');
    if (!sessionStart) return false;

    const startTime = parseInt(sessionStart, 10);
    if (isNaN(startTime)) return false;

    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000;
    if (now - startTime > maxAge) return false;

    return true;
  } catch {
    return false;
  }
}

export function validateInputLength(
  input: string,
  maxLength = 1000,
  minLength = 0
): { isValid: boolean; error?: string } {
  if (input.length < minLength) {
    return { isValid: false, error: `Input must be at least ${minLength} characters` };
  }
  if (input.length > maxLength) {
    return { isValid: false, error: `Input must not exceed ${maxLength} characters` };
  }
  return { isValid: true };
}

export function sanitizeObject<T extends Record<string, unknown>>(obj: T, options?: { placeNameKeys?: string[] }): T {
  const placeNameKeys = new Set(options?.placeNameKeys ?? []);
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key] = placeNameKeys.has(key) ? sanitizePlaceName(value) : sanitizeInput(value);
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeObject(value as Record<string, unknown>, options);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map((item) =>
        typeof item === 'string'
          ? placeNameKeys.has(key)
            ? sanitizePlaceName(item)
            : sanitizeInput(item)
          : item !== null && typeof item === 'object'
            ? sanitizeObject(item as Record<string, unknown>, options)
            : item
      );
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized as T;
}

export function createSecureIdentifier(input: string): string {
  const encoder = new TextEncoder();
  const data = encoder.encode(input + Date.now().toString());
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data[i];
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

export function isValidUUID(id: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
}

interface StoredRateLimitData {
  count: number;
  resetTime: number;
}

export class PersistentRateLimiter extends RateLimiter {
  private storageKey: string;
  private persistMaxAttempts: number;
  private persistWindowMs: number;

  constructor(maxAttempts = 5, windowMs: number = 15 * 60 * 1000, storageKey = 'rate_limit') {
    super(maxAttempts, windowMs);
    this.storageKey = storageKey;
    this.persistMaxAttempts = maxAttempts;
    this.persistWindowMs = windowMs;
  }

  private getStoredData(): Map<string, StoredRateLimitData> {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Map(Object.entries(parsed));
      }
    } catch {
      return new Map();
    }
    return new Map();
  }

  private saveData(data: Map<string, StoredRateLimitData>): void {
    try {
      const obj = Object.fromEntries(data);
      localStorage.setItem(this.storageKey, JSON.stringify(obj));
    } catch {
      return;
    }
  }

  canAttempt(key: string): boolean {
    const now = Date.now();
    const data = this.getStoredData();
    const attempt = data.get(key);

    if (!attempt || now > attempt.resetTime) {
      data.set(key, { count: 1, resetTime: now + this.persistWindowMs });
      this.saveData(data);
      return true;
    }

    if (attempt.count >= this.persistMaxAttempts) {
      return false;
    }

    attempt.count++;
    data.set(key, attempt);
    this.saveData(data);
    return true;
  }

  reset(key: string): void {
    const data = this.getStoredData();
    data.delete(key);
    this.saveData(data);
  }
}

export type SecurityEvent =
  | 'AUTH_SUCCESS'
  | 'AUTH_FAILURE'
  | 'RATE_LIMIT_HIT'
  | 'MALICIOUS_INPUT_BLOCKED'
  | 'OWNERSHIP_VIOLATION'
  | 'SESSION_EXPIRED'
  | 'SESSION_BOOTSTRAPPED'
  | 'CSRF_FAILURE'
  | 'VALIDATION_FAILURE';

export interface AuditLogger {
  log(event: SecurityEvent, metadata: Record<string, unknown>): void;
}

class AuditLoggerImpl implements AuditLogger {
  private pendingLogs: Array<{ event: SecurityEvent; metadata: Record<string, unknown> }> = [];
  private isSending = false;
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  log(event: SecurityEvent, metadata: Record<string, unknown>): void {
    this.pendingLogs.push({ event, metadata });
    this.flushLogs();
  }

  private async flushLogs(): Promise<void> {
    if (this.isSending || this.pendingLogs.length === 0) {
      return;
    }

    this.isSending = true;
    const logsToSend = [...this.pendingLogs];
    this.pendingLogs = [];

    for (const log of logsToSend) {
      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        if (!supabaseUrl) {
          continue;
        }

        let jwt: string | null = null;
        try {
          const sessionData = localStorage.getItem('sb-' + new URL(supabaseUrl).hostname.split('.')[0] + '-auth-token');
          if (sessionData) {
            const session = JSON.parse(sessionData);
            jwt = session?.access_token || null;
          }
        } catch {
          jwt = null;
        }

        const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (anonKey) {
          headers['apikey'] = anonKey;
        }
        if (jwt) {
          headers['Authorization'] = `Bearer ${jwt}`;
        }

        await fetch(`${supabaseUrl}/functions/v1/audit-log`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            event_type: log.event,
            metadata: log.metadata,
            session_id: this.sessionId,
          }),
        });
      } catch {
        continue;
      }
    }

    this.isSending = false;

    if (this.pendingLogs.length > 0) {
      setTimeout(() => this.flushLogs(), 1000);
    }
  }
}

export const auditLogger: AuditLogger = new AuditLoggerImpl();
